import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CustomDropdown } from '../custom-dropdown';
import {
  Container,
  ControlButton,
  ControlsWrapper,
  InputContainer,
  PrefixSuffix,
  StyledInput,
} from './styled';
import type { NumberInputWithUnitProps, UnitOption } from './types';

const DEFAULT_UNITS: UnitOption[] = [
  { value: 'px', label: 'Pixels', abbreviation: 'px' },
  { value: 'em', label: 'Em', abbreviation: 'em' },
  { value: 'rem', label: 'Rem', abbreviation: 'rem' },
  { value: '%', label: 'Percent', abbreviation: '%' },
];

export const NumberInputWithUnit: React.FC<NumberInputWithUnitProps> = ({
  variant = 'outline',
  size = 'medium',
  error = false,
  className,
  testId,
  styleProps,
  value: controlledValue,
  selectedUnit,
  onChange,
  onUnitChange,
  unitOptions = DEFAULT_UNITS,
  unitPosition = 'right',
  min,
  max,
  step = 1,
  precision = 0,
  showControls = false,
  allowNegative = true,
  prefix,
  suffix,
  unitDisabled = false,
  placeholder,
  disabled,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState<string>('');
  const [internalUnit, setInternalUnit] = useState<string>(unitOptions[0]?.value ?? '');

  const parseValueWithUnit = useCallback(
    (value: string | number | undefined) => {
      if (value === undefined || value === null || value === '') {
        return { numericValue: '', unit: unitOptions[0]?.value ?? '' };
      }

      const valueStr = String(value);

      const sortedUnits = [...unitOptions].sort((a, b) => b.value.length - a.value.length);
      const unitMatch = sortedUnits.find((unit) => valueStr.endsWith(unit.value));

      if (unitMatch) {
        const numericPart = valueStr.slice(0, -unitMatch.value.length);
        return { numericValue: numericPart, unit: unitMatch.value };
      }

      return { numericValue: valueStr, unit: (internalUnit || unitOptions[0]?.value) ?? '' };
    },
    [unitOptions, internalUnit]
  );

  const isControlled = controlledValue !== undefined;

  const parsedControlledValue = parseValueWithUnit(controlledValue);

  const currentValue = isControlled ? parsedControlledValue.numericValue : internalValue;
  const currentUnit = selectedUnit ?? (isControlled ? parsedControlledValue.unit : internalUnit);

  useEffect(() => {
    if (!isControlled && controlledValue !== undefined) {
      const parsed = parseValueWithUnit(controlledValue);
      setInternalValue(parsed.numericValue);
      setInternalUnit(parsed.unit);
    }
  }, [controlledValue, isControlled, parseValueWithUnit]);

  const formatNumber = useCallback(
    (num: number): string => {
      if (precision === 0) {
        return String(Math.round(num));
      }
      return num.toFixed(precision);
    },
    [precision]
  );

  const parseValue = useCallback(
    (inputValue: string): number | undefined => {
      if (!inputValue || inputValue === '') return undefined;

      const cleaned = inputValue.replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);

      if (isNaN(parsed)) return undefined;

      if (!allowNegative && parsed < 0) return 0;
      if (min !== undefined && parsed < min) return min;
      if (max !== undefined && parsed > max) return max;

      return parsed;
    },
    [allowNegative, min, max]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;

      const numericPattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;

      if (inputValue === '' || numericPattern.test(inputValue)) {
        if (!isControlled) {
          setInternalValue(inputValue);
        }

        const combinedValue = inputValue === '' ? '' : `${inputValue}${currentUnit}`;
        onChange?.(combinedValue);
      }

    },
    [isControlled, onChange, currentUnit, allowNegative]
  );

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      if (!selectedUnit) {
        setInternalUnit(newUnit);
      }

      onUnitChange?.(newUnit);

      const combinedValue = currentValue === '' ? '' : `${currentValue}${newUnit}`;
      onChange?.(combinedValue);
    },
    [currentValue, onChange, onUnitChange, selectedUnit]
  );

  const adjustValue = useCallback(
    (direction: 'up' | 'down') => {
      const currentParsedValue = parseValue(currentValue) ?? 0;
      const adjustment = direction === 'up' ? step : -step;
      const newValue = currentParsedValue + adjustment;
      const constrainedValue = parseValue(String(newValue)) ?? 0;
      const formattedValue = formatNumber(constrainedValue);

      if (!isControlled) {
        setInternalValue(formattedValue);
      }

      const combinedValue = `${formattedValue}${currentUnit}`;
      onChange?.(combinedValue);
    },
    [parseValue, currentValue, step, formatNumber, isControlled, onChange, currentUnit]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (showControls) {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          adjustValue('up');
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          adjustValue('down');
        }
      }

      props.onKeyDown?.(event);
    },
    [showControls, adjustValue, props]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const char = event.key;
      const currentValue2 = (event.target as HTMLInputElement).value;

      if (event.ctrlKey || event.metaKey || char.length > 1) {
        return;
      }

      if (/\d/.test(char)) {
        return;
      }

      if (char === '.' && !currentValue2.includes('.') && precision > 0) {
        return;
      }

      if (char === '-' && allowNegative && currentValue2.length === 0) {
        return;
      }

      event.preventDefault();

      props.onKeyPress?.(event);
    },
    [allowNegative, precision, props]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const parsedValue = parseValue(currentValue);
      if (parsedValue !== undefined) {
        const formattedValue = formatNumber(parsedValue);
        if (!isControlled) {
          setInternalValue(formattedValue);
        }
      }

      props.onBlur?.(event);
    },
    [parseValue, currentValue, formatNumber, isControlled, props]
  );

  const hasPrefix = Boolean(prefix);
  const hasSuffix = Boolean(suffix);

  return (
    <Container
      className={className}
      $fullWidth={props.style?.width === '100%'}
      $styleProps={styleProps}
      $unitPosition={unitPosition}
      $error={error}
    >
      {unitPosition === 'left' && (
        <CustomDropdown
          options={unitOptions}
          value={currentUnit}
          onChange={handleUnitChange}
          size={size}
          position='left'
          disabled={unitDisabled || disabled}
          styleProps={styleProps}
        />
      )}

      <InputContainer
        $variant={variant}
        $size={size}
        $error={error}
        $hasPrefix={hasPrefix}
        $hasSuffix={hasSuffix}
        $unitPosition={unitPosition}
        $styleProps={styleProps}
      >
        {hasPrefix && (
          <PrefixSuffix $position='prefix' $styleProps={styleProps}>
            {prefix}
          </PrefixSuffix>
        )}

        <StyledInput
          ref={inputRef}
          type='text'
          inputMode='decimal'
          $variant={variant}
          $size={size}
          $error={error}
          $hasPrefix={hasPrefix}
          $hasSuffix={hasSuffix}
          $unitPosition={unitPosition}
          $styleProps={styleProps}
          value={currentValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          {...props}
        />

        {hasSuffix && (
          <PrefixSuffix $position='suffix' $styleProps={styleProps}>
            {suffix}
          </PrefixSuffix>
        )}

        {showControls && (
          <ControlsWrapper>
            <ControlButton
              type='button'
              $size={size}
              $disabled={disabled}
              $styleProps={styleProps}
              onClick={() => adjustValue('up')}
              disabled={disabled}
              tabIndex={-1}
              aria-label='Increment value'
            >
              <ChevronUpIcon width={12} height={12} />
            </ControlButton>
            <ControlButton
              type='button'
              $size={size}
              $disabled={disabled}
              $styleProps={styleProps}
              onClick={() => adjustValue('down')}
              disabled={disabled}
              tabIndex={-1}
              aria-label='Decrement value'
            >
              <ChevronDownIcon width={12} height={12} />
            </ControlButton>
          </ControlsWrapper>
        )}
      </InputContainer>

      {unitPosition === 'right' && (
        <CustomDropdown
          options={unitOptions}
          value={currentUnit}
          onChange={handleUnitChange}
          size={size}
          position='right'
          disabled={unitDisabled || disabled}
          styleProps={styleProps}
        />
      )}
    </Container>
  );
};